import { useCallback, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import * as endpoints from 'utils/api/endpoints';
import { httpMethods, useFetchData } from 'utils/hooks/use-fetch-data';
import { FAV_AND_RECENT_TYPE_EVENT, FAV_AND_RECENT_TYPE_TEAM } from 'widgets/favorites/constant';

import { FavoriteItemType, SelectItemHandler } from '../search-dropdown/types';

export type UseRecentSearchRedirectParams = {
    onTeamSelect?: (teamId: number) => void;
    onEventClick?: (eventId?: number) => void;
};

type SelectedItem = { id: number; type: FavoriteItemType };

/**
 * A chosen search result is saved as a recent search, then opened (also when the saving fails): a team and an event by the callbacks of the panel,
 * a player on its scout page (reloaded when another player page is open, so its data are fetched anew).
 */
export const useRecentSearchRedirect = ({ onTeamSelect, onEventClick }: UseRecentSearchRedirectParams) => {
    const history = useHistory();
    const location = useLocation();
    const [selectedItem, setSelectedItem] = useState<SelectedItem | null>(null);

    const openSelectedItem = useCallback(() => {
        if (!selectedItem) {
            return;
        }
        setSelectedItem(null);
        const { id, type } = selectedItem;
        if (type === FAV_AND_RECENT_TYPE_TEAM) {
            onTeamSelect?.(id);
        } else if (type === FAV_AND_RECENT_TYPE_EVENT) {
            onEventClick?.(id);
        } else if (location.pathname.includes('/scout/players/')) {
            window.location.assign(`/scout/players/${id}`);
        } else {
            history.push(`/scout/players/${id}`);
        }
    }, [selectedItem, history, location, onTeamSelect, onEventClick]);

    // The item opens also when saving the recent search fails: before, it stayed unopened and the next clicks did
    // nothing (the request kept its initial condition)
    const handleSaveError = useCallback(
        (error: unknown) => {
            // eslint-disable-next-line no-console -- the recent search is not saved; the item opens anyway
            console.error('Saving the recent search failed:', error);
            openSelectedItem();
        },
        [openSelectedItem]
    );

    useFetchData({
        method: httpMethods.post,
        url: endpoints.recentSearch,
        initialCondition: !!selectedItem,
        payload: { itemEntityId: selectedItem?.id, itemType: selectedItem?.type },
        responseHandler: openSelectedItem,
        onRequestError: handleSaveError
    });

    const handleSelect: SelectItemHandler = useCallback((id, type) => setSelectedItem({ id, type }), []);

    return handleSelect;
};
